"use client";

import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import dynamic from "next/dynamic";
import { Badge, Divider } from "@/components/ui";
import { useUser } from "@/contexts/user-provider";
import { formatDate } from "@/lib/format-data";
import { PERMISSIONS, hasPermission } from "@/lib/permissions";
import { UserTiny } from "@/types/user";
import { PatientSidebarSectionSkeleton } from "./section-skeleton";
import { formQueryOptions } from "@/api/forms/form-query";
const SubmissionsDrawer = dynamic(() => import("@/components/form/submissions-drawer"), {
  ssr: false
});
interface Props {
  patient: UserTiny;
}
interface ViewingSubmission {
  form: {
    id: string;
    name: string;
  };
  answeredFormId: string;
  isCompleted: boolean;
}
export default function FormsSection(t0) {
  const $ = _c(25);
  const {
    patient
  } = t0;
  const patientId = patient.id;
  const {
    currentUser
  } = useUser();
  let t1;
  if ($[0] !== currentUser) {
    t1 = hasPermission(currentUser, PERMISSIONS.FORMS_SUBMISSIONS_READ);
    $[0] = currentUser;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const canViewForms = t1;
  const [viewingSubmission, setViewingSubmission] = useState(null);
  let t2;
  if ($[2] !== patientId) {
    t2 = formQueryOptions.submissions(patientId);
    $[2] = patientId;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = !!patientId && canViewForms;
  let t4;
  if ($[4] !== t2 || $[5] !== t3) {
    t4 = {
      ...t2,
      enabled: t3
    };
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const {
    data: formSubmissions,
    isPending: isPendingSubmissions
  } = useQuery(t4);
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = formQueryOptions.table(true);
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== canViewForms) {
    t6 = {
      ...t5,
      enabled: canViewForms
    };
    $[8] = canViewForms;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const {
    data: forms,
    isPending: isPendingForms
  } = useQuery(t6);
  if (!canViewForms) {
    let t7;
    if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <p className="text-caption text-muted-foreground">You don't have permission to view form submissions</p>;
      $[10] = t7;
    } else {
      t7 = $[10];
    }
    return t7;
  }
  if (isPendingSubmissions || isPendingForms) {
    let t7;
    if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <PatientSidebarSectionSkeleton />;
      $[11] = t7;
    } else {
      t7 = $[11];
    }
    return t7;
  }
  if (!formSubmissions?.data || formSubmissions.data.length === 0) {
    let t7;
    if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <p className="text-caption text-muted-foreground">No forms submitted</p>;
      $[12] = t7;
    } else {
      t7 = $[12];
    }
    return t7;
  }
  let t7;
  if ($[13] !== formSubmissions.data || $[14] !== forms) {
    let t8;
    if ($[16] !== formSubmissions.data.length || $[17] !== forms) {
      t8 = (submission, index) => {
        const form = forms?.find(f => f.id === submission.formId);
        return <div key={submission.answeredFormId || index} className="mb-3"><div className="flex items-center gap-2"><button type="button" className="text-left text-body-strong text-foreground hover:underline" onClick={() => setViewingSubmission({
              form: {
                id: submission.formId,
                name: form?.name || submission.formId
              },
              answeredFormId: submission.answeredFormId,
              isCompleted: submission.isCompleted
            })}>{form?.name || submission.formId}</button>{!submission.isCompleted && <Badge variant="neutral" className="w-fit">Incomplete</Badge>}</div><p className="text-caption text-foreground-secondary">{submission.isCompleted ? <span>{"Submitted on"}</span> : <span>{"Started on"}</span>}{" "}<span>{formatDate(submission.submissionDate)}</span></p>{submission.isExpired && <p className="text-caption text-destructive">Expired</p>}{!submission.isExpired && submission.expiresAt && <p className="text-caption text-muted-foreground">Expires {formatDate(submission.expiresAt)}</p>}{index !== formSubmissions.data.length - 1 && <Divider className="my-2" />}</div>;
      };
      $[16] = formSubmissions.data.length;
      $[17] = forms;
      $[18] = t8;
    } else {
      t8 = $[18];
    }
    t7 = formSubmissions.data.map(t8);
    $[13] = formSubmissions.data;
    $[14] = forms;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[19] !== patient || $[20] !== viewingSubmission) {
    t8 = viewingSubmission && <SubmissionsDrawer form={viewingSubmission.form} isOpen={!!viewingSubmission} onClose={() => setViewingSubmission(null)} patient={patient} submission={{
      isCompleted: viewingSubmission.isCompleted
    }} answeredFormId={viewingSubmission.answeredFormId} />;
    $[19] = patient;
    $[20] = viewingSubmission;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== t7 || $[23] !== t8) {
    t9 = <div className="max-h-60 overflow-y-auto">{t7}{t8}</div>;
    $[22] = t7;
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  return t9;
}